import { gameActionBus, type SkillSlot } from './actions';
import {installInputCancellation} from './inputCancellation';

function shouldShowTouchControls(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('touch') === '1'
    || navigator.maxTouchPoints > 0
    || window.matchMedia('(pointer: coarse)').matches;
}

export function installTouchInput(doc: Document = document): () => void {
  const root = doc.getElementById('touch-controls');
  const stick = doc.getElementById('move-stick');
  const knob = doc.getElementById('move-stick-knob');
  if (!root || !stick || !knob) return () => undefined;

  if (shouldShowTouchControls()) doc.body.classList.add('touch-enabled');
  const cleanups: Array<() => void> = [];

  let activePointer: number | null = null;
  const updateStick = (event: PointerEvent) => {
    const rect = stick.getBoundingClientRect();
    const radius = rect.width * 0.36;
    const center = rect.left + rect.width / 2;
    const raw = (event.clientX - center) / radius;
    const axis = Math.max(-1, Math.min(1, raw));
    const depth = Math.max(-1, Math.min(1, (event.clientY - rect.top - rect.height / 2) / radius));
    knob.style.transform = `translate(${axis * radius}px, ${depth * radius}px)`;
    gameActionBus.emit({ type: 'move', axis });
    gameActionBus.emit({ type: 'move-depth', axis: depth });
  };
  const onStickDown = (event: PointerEvent) => {
    if (activePointer !== null||doc.body.classList.contains('touch-layout-edit')) return;
    activePointer = event.pointerId;
    stick.setPointerCapture(event.pointerId);
    updateStick(event);
    event.preventDefault();
  };
  const onStickMove = (event: PointerEvent) => {
    if (event.pointerId === activePointer) updateStick(event);
  };
  const endStick = (event: PointerEvent) => {
    if (event.pointerId !== activePointer) return;
    activePointer = null;
    knob.style.transform = 'translate(0, 0)';
    gameActionBus.emit({ type: 'move', axis: 0 });
    gameActionBus.emit({ type: 'move-depth', axis: 0 });
  };
  stick.addEventListener('pointerdown', onStickDown);
  stick.addEventListener('pointermove', onStickMove);
  stick.addEventListener('pointerup', endStick);
  stick.addEventListener('pointercancel', endStick);
  stick.addEventListener('lostpointercapture', endStick);
  const reset=()=>{activePointer=null;knob.style.transform='translate(0, 0)';gameActionBus.emit({type:'move',axis:0});gameActionBus.emit({type:'move-depth',axis:0});};
  window.addEventListener('touch-layout-mode',reset);
  const cancel=installInputCancellation(()=>{reset();gameActionBus.emit({type:'sprint',active:false});},window,doc);
  cleanups.push(() => {
    stick.removeEventListener('pointerdown', onStickDown);
    stick.removeEventListener('pointermove', onStickMove);
    stick.removeEventListener('pointerup', endStick);
    stick.removeEventListener('pointercancel', endStick);
    stick.removeEventListener('lostpointercapture', endStick);
    window.removeEventListener('touch-layout-mode',reset);cancel();
  });

  const buttonHandlers = new Map<Element, EventListener>();
  for (const button of doc.querySelectorAll<HTMLElement>('[data-game-action]')) {
    const handler: EventListener = (rawEvent) => {
      const event = rawEvent as PointerEvent;
      if(doc.body.classList.contains('touch-layout-edit'))return;
      const name = button.dataset.gameAction;
      if (name === 'attack') gameActionBus.emit({ type: 'attack' });
      else if (name === 'jump') gameActionBus.emit({ type: 'jump' });
      else if (name === 'dodge') gameActionBus.emit({ type: 'dodge' });
      else if (name === 'sprint') {button.setPointerCapture(event.pointerId);gameActionBus.emit({type:'sprint',active:true});}
      else if (name?.startsWith('skill-')) {
        const slot = Number(name.slice(-1)) - 1;
        if (slot >= 0 && slot <= 3) gameActionBus.emit({ type: 'skill', slot: slot as SkillSlot });
      }
      event.preventDefault();
    };
    button.addEventListener('pointerdown', handler);
    if(button.dataset.gameAction==='sprint'){
      const release=()=>gameActionBus.emit({type:'sprint',active:false});
      button.addEventListener('pointerup',release);button.addEventListener('pointercancel',release);button.addEventListener('lostpointercapture',release);window.addEventListener('gameplay-input-reset',release);window.addEventListener('blur',release);
      cleanups.push(()=>{button.removeEventListener('pointerup',release);button.removeEventListener('pointercancel',release);button.removeEventListener('lostpointercapture',release);window.removeEventListener('gameplay-input-reset',release);window.removeEventListener('blur',release);release();});
    }
    buttonHandlers.set(button, handler);
  }
  cleanups.push(() => buttonHandlers.forEach((handler, button) => button.removeEventListener('pointerdown', handler)));

  return () => cleanups.forEach((cleanup) => cleanup());
}
